// The felt.
//
// STRUCTURE (load-bearing — lattice.ts measures it):
//
//   grid = 14 EQUAL rows x [ even-money rail | 3 number columns | dozen rail ]
//     row  1        the ZERO band, spanning all three number columns
//     rows 2..13    the 36 numbers, [1,2,3] / [4,5,6] / ...
//     row  14       the three column ("2~1") bets
//     column 1      the SIX even-money bets, two rows each
//     column 5      the THREE dozens, four rows each
//
// Every row is the SAME height. That uniform pitch is what lets one piece of
// maths in lattice.ts serve centres, edges and corners — including the zero
// line, which is why 0/1, 0/2, 0/3, the trios and the first-four come out for
// free instead of needing their own special cases. Do not make the zero band
// taller to match a screenshot more closely: it would cost six special cases
// in the hit test to buy about six pixels.
//
// WHY THE RAILS ARE ON OPPOSITE SIDES
// -----------------------------------
// This mirrors the reference table: even-money down the LEFT as six stacked
// segments, dozens down the RIGHT as three, columns along the bottom. An
// earlier build had the dozens on the left and the even-money in a horizontal
// band under the felt. Both are legitimate roulette layouts; only one is the
// one being copied.
//
// GAP IS ZERO. Adjacent cells each paint a 0.75px inset ring, so a shared
// border sums to the 1.5px stroke the reference draws, and the ~39px the old
// 3px gutters were eating goes back into row height. Nothing in lattice.ts
// divides by gap, so every marker formula degenerates cleanly — a marker on a
// line now lands exactly ON the shared border, which is more correct, not less.
//
// There are NO invisible hit-zone buttons. Splits, streets, corners and six
// lines are resolved from the pointer position against the DOM cell under the
// finger (see aimAt in Page.tsx). The cells here are for painting and for the
// keyboard path only.
import { forwardRef } from 'react';
import { colourOf, type BetKind, RETURN_X } from './engine';
import { columnBet, dozenBet, EVEN_BETS, type PendingBet } from './lattice';

// The outside bets themselves live in lattice.ts: the winning-zone highlight has
// to derive from them and lattice.ts is already imported here, so defining them
// in this file and importing them back would be a cycle. Re-exported so existing
// call sites keep working.
export { columnBet, dozenBet, EVEN_BETS } from './lattice';

/** THE BOARD IS FIVE EQUAL COLUMNS.
 *
 *  Measured off docs/assets/roulette_ref/ref1.jpeg: the vertical gridlines sit
 *  at x = 127.5 / 191.5 / 255.5 / 319.5 / 383.5 / 447.5 — five columns of
 *  exactly 64.0 px, at every y, with no taper. The rails are NOT narrower than
 *  the number columns; they are the same width.
 *
 *  The previous build used 44px rails against 82px number columns, which made
 *  the rails 31% too narrow and the cells 28% too wide — the single reason
 *  every cell read the wrong shape however the colours were tuned.
 *
 *  Because the columns are now `repeat(5, 1fr)`, the rail width is no longer a
 *  constant: it is one fifth of whatever the grid measures. Anything that needs
 *  it reads it at the point of use. RAIL_FRAC is the only thing exported. */
export const RAIL_FRAC = 0.2;
export const GAP = 0;

const ROWS = Array.from({ length: 12 }, (_, r) => [r * 3 + 1, r * 3 + 2, r * 3 + 3]);

/** Rails and the 2~1 strip stay ordinary DOM buttons carrying their bet as data
 *  attributes: the gesture layer finds them with elementFromPoint, so one
 *  pipeline serves the lattice and the rails without a second code path. */
function betAttrs(b: PendingBet) {
  return {
    // data-bet-id is what the winning-zone wash keys on. It is the SAME
    // canonical id makeBet() produces, so a rail can never light for a bet the
    // money layer would not have paid.
    'data-bet-id': b.id,
    'data-bet-kind': b.kind,
    'data-bet-numbers': b.numbers.join(','),
    'data-bet-title': b.title,
  } as const;
}

/** The diamond on the RED / BLACK rails.
 *
 *  Inline SVG, never the characters U+2666 / U+25C6: several Android font
 *  stacks resolve those to a colour emoji, which then ignores `color` entirely
 *  — and the winning-zone highlight has to be able to turn this glyph gold. */
function Diamond({ tone }: { tone: 'red' | 'black' }) {
  return (
    <svg viewBox="0 0 38 48" className={`rlt-glyph rlt-glyph--${tone}`} aria-hidden focusable="false">
      <path d="M19 0 L38 24 L19 48 L0 24Z" />
    </svg>
  );
}

/** A rail label, rotated to read bottom-to-top.
 *
 *  `writing-mode: vertical-rl` CANNOT do this job: given two lines of different
 *  size it lays them out as two side-by-side vertical columns instead of
 *  stacking one above the other. A rotated flex column stacks correctly and
 *  keeps the reference's small-over-large pairing. The rail button must NOT be
 *  overflow:hidden, or the rotated box gets clipped by its own cell. */
function RailLabel({ big, small, order = 'big-first', num = false, tight = false }: {
  big: string; small?: string; order?: 'big-first' | 'small-first';
  /** MEASURED: the reference sets the big rail labels at TWO sizes, not one —
   *  cap 20px for EVEN / ODD / 1st 12 / 2nd 12 / 3rd 12, but cap 17px for the
   *  range labels 1~18 and 19~36 (and for 2~1, which .rlt-rail--flat already
   *  handles). One factor for all of them left the ranges 19% oversized. */
  tight?: boolean;
  /** MEASURED: the reference sets the NUMERIC big labels (1~18, 19~36, 1st 12,
   *  2nd 12, 3rd 12, 2~1) in the same high-contrast serif as the disc numerals —
   *  bracketed serifs, thick/thin modulation, a ball terminal on the 2 — but sets
   *  EVEN, ODD and every small line in a geometric sans. One flag, because they
   *  share a class otherwise. */
  num?: boolean;
}) {
  const bigEl = (
    <span key="b" className={`rlt-rail-big${num ? ' rlt-rail-big--num' : ''}${tight ? ' rlt-rail-big--tight' : ''}`}>
      {big}
    </span>
  );
  const smallEl = small ? <span key="s" className="rlt-rail-small">{small}</span> : null;
  return (
    <span className="rlt-rail-rot">
      <span className="rlt-rail-stack">
        {order === 'big-first' ? [bigEl, smallEl] : [smallEl, bigEl]}
      </span>
    </span>
  );
}

type Props = {
  amountOf: (id: string) => number;
  disabled: boolean;
  activeId: string | null;
  /** Cells to paint as "covered by the bet being placed", keyed "r:c". */
  litCells: Set<string>;
  winner: number | null;
  /** Ids of every OUTSIDE bet that just won — the rails wash gold together with
   *  the winning number, which is how the reference shows a result. Empty
   *  except during the result hold. */
  winIds: Set<string>;
  onKeyPlace: (r: number, c: number, e: React.KeyboardEvent) => void;
  focusCell: { r: number; c: number };
  setFocusCell: (v: { r: number; c: number }) => void;
  children?: React.ReactNode;   // the overlay
};

/** The grid itself. `ref` is what lattice.measureLattice() measures — so it must
 *  have NO padding and NO border, or the lattice origin is wrong. */
const Board = forwardRef<HTMLDivElement, Props>(function Board(
  { amountOf, disabled, activeId, litCells, winner, winIds, onKeyPlace, focusCell, setFocusCell, children }, ref,
) {
  const railCls = (b: PendingBet) =>
    `rlt-rail${activeId === b.id ? ' act' : ''}${winIds.has(b.id) ? ' rlt-won' : ''}${disabled ? ' off' : ''}`;

  const Cell = ({ n, r, c }: { n: number; r: number; c: number }) => {
    const focused = focusCell.r === r && focusCell.c === c;
    return (
      <button
        type="button"
        tabIndex={focused ? 0 : -1}
        aria-label={`Number ${n}`}
        data-n={n}
        data-c={colourOf(n)}
        onKeyDown={(e) => onKeyPlace(r, c, e)}
        onFocus={() => setFocusCell({ r, c })}
        style={{ gridColumn: c + 2, gridRow: r + 1 }}
        className={`rlt-cell${winner === n ? ' rlt-won-cell' : ''}${litCells.has(`${r}:${c}`) ? ' lit' : ''}`}
      >
        <span className="rlt-cell-n">{n}</span>
        {/* The always-on price, whispered: sharp eyes learn the multiplier
            without the Pays overlay; everyone else never notices it. The Pays
            button is where it speaks at full volume. (Owner, 2026-09-01.) */}
        <span className="rlt-cell-mult" aria-hidden>X{RETURN_X.straight}</span>
      </button>
    );
  };

  return (
    <div
      ref={ref}
      className="rlt-grid relative grid w-full"
      // 14 EQUAL fractional rows and NO min-height: the board sizes itself to
      // whatever height it is given, so the whole felt is ALWAYS visible and
      // there is nothing to scroll. Scrolling was the mistake in an earlier
      // build — on a real phone that gesture fights placement, and it hid
      // numbers that had been visible.
      style={{
        // minmax(0, 1fr), never plain 1fr: `1fr` is `minmax(auto, 1fr)`, so a
        // column grows past its share whenever its content asks to — the
        // rotated rail labels and the wide numerals pushed the five columns to
        // 45 / 57 / 48 instead of five equal fifths.
        gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
        gridTemplateRows: 'repeat(14, minmax(0, 1fr))',
        gap: GAP,
        // Height and aspect ratio live in CSS (.rlt-grid) so the 3/7 board
        // shape survives; an inline height:100% would override it.
      }}
      aria-label="Roulette betting board"
    >
      {/* ── the four dead corners ──────────────────────────────────────────
          Rail columns x rows 1 and 14 belong to no bet. They are filled with an
          inert frame rather than left empty — and deliberately NOT by
          stretching a neighbour into them: extending "1~18 Small" up into the
          zero row would place a real bet on a mis-tap, and a tap that silently
          does nothing reads to a player as a broken game. */}
      {/* Measured: at y=345/365 the only verticals between x=100 and x=500 are
          191.5 and 383.5 — there is NO line at 127.5 or 447.5. Same at the
          bottom row. The four corners are bare felt; we were drawing four boxes
          that do not exist. The elements stay only so the grid keeps its shape
          and they remain inert to hit-testing. */}
      {([[1, 1], [5, 1], [1, 14], [5, 14]] as const).map(([col, row]) => (
        <div key={`x${col}-${row}`} className="rlt-corner" style={{ gridColumn: col, gridRow: row }} aria-hidden />
      ))}

      {/* ── row 1 — the zero band, at full row pitch so it is a real target and
             so the lattice pitch stays uniform.

             The zero's disc is a NORMAL disc, the same 42x37 as every number —
             measured centred at (287.5, 367.0) in ref1. It is not a pill; an
             earlier build stretched it across all three columns. ── */}
      <button
        type="button"
        tabIndex={focusCell.r === 0 ? 0 : -1}
        aria-label="Number 0"
        data-n={0}
        data-c="green"
        onKeyDown={(e) => onKeyPlace(0, 0, e)}
        onFocus={() => setFocusCell({ r: 0, c: 0 })}
        style={{ gridColumn: '2 / span 3', gridRow: 1 }}
        className={`rlt-cell rlt-cell--zero${winner === 0 ? ' rlt-won-cell' : ''}${litCells.has('0:0') ? ' lit' : ''}`}
      >
        <span className="rlt-cell-n">0</span>
        <span className="rlt-cell-mult" aria-hidden>X{RETURN_X.straight}</span>
      </button>

      {/* ── column 1 — the six even-money bets, two number-rows each.
             EVEN_BETS is already in the reference's top-to-bottom order
             (low, even, red, black, odd, high), so this iterates it as-is. ── */}
      {EVEN_BETS.map((b, i) => {
        const amt = amountOf(b.id);
        const label =
          b.kind === 'low' ? <RailLabel big="1–18" small="Small" num tight />
            : b.kind === 'high' ? <RailLabel big="19–36" small="Big" num tight />
              : b.kind === 'red' ? <Diamond tone="red" />
                : b.kind === 'black' ? <Diamond tone="black" />
                  : <RailLabel big={b.kind === 'even' ? 'EVEN' : 'ODD'} />;
        return (
          <button
            key={b.id} type="button" {...betAttrs(b)} data-disabled={disabled ? '1' : '0'}
            aria-label={`${b.title} — ${b.sub}`}
            style={{ gridColumn: 1, gridRow: `${i * 2 + 2} / span 2` }}
            className={`${railCls(b)}${i === 0 ? ' rlt-rail--tl' : ''}${i === EVEN_BETS.length - 1 ? ' rlt-rail--bl' : ''}`}
          >
            {label}
            <span className="rlt-rail-mult" aria-hidden>X{RETURN_X.red}</span>
            {amt > 0 && <RailChip amount={amt} />}
          </button>
        );
      })}

      {/* ── rows 2..13 — the numbers ── */}
      {ROWS.map((row, r) => row.map((n, c) => <Cell key={n} n={n} r={r + 1} c={c} />))}

      {/* ── column 5 — the three dozens, four number-rows each, sitting beside
             the twelve numbers each one actually covers ── */}
      {([1, 2, 3] as const).map((d) => {
        const b = dozenBet(d);
        const amt = amountOf(b.id);
        return (
          <button
            key={b.id} type="button" {...betAttrs(b)} data-disabled={disabled ? '1' : '0'}
            aria-label={`${b.title} — ${b.sub}`}
            style={{ gridColumn: 5, gridRow: `${(d - 1) * 4 + 2} / span 4` }}
            className={`${railCls(b)}${d === 1 ? ' rlt-rail--tr' : ''}${d === 3 ? ' rlt-rail--br' : ''}`}
          >
            <RailLabel big={['1–12', '13–24', '25–36'][d - 1]} num />
            <span className="rlt-rail-mult" aria-hidden>X{RETURN_X.dozen}</span>
            {amt > 0 && <RailChip amount={amt} />}
          </button>
        );
      })}

      {/* ── row 14 — the column bets, under the columns they cover. Horizontal,
             so these are the one rail that is not rotated. ── */}
      {([1, 2, 3] as const).map((c) => {
        const b = columnBet(c);
        const amt = amountOf(b.id);
        return (
          <button
            key={b.id} type="button" {...betAttrs(b)} data-disabled={disabled ? '1' : '0'}
            aria-label={`${b.title} — ${b.sub}`}
            style={{ gridColumn: c + 1, gridRow: 14 }}
            className={`${railCls(b)} rlt-rail--flat`}
          >
            <span className="rlt-rail-big rlt-rail-arrow" aria-hidden>↑</span>
            <span className="rlt-rail-mult" aria-hidden>X{RETURN_X.dozen}</span>
            {amt > 0 && <RailChip amount={amt} />}
          </button>
        );
      })}

      {children}
    </div>
  );
});

/** The stake sitting on a rail. Deliberately the SAME chip object as the tray
 *  and the felt — "what you pick up is what lands" holds everywhere on the
 *  board, so a rail bet never looks like a different kind of money. */
function RailChip({ amount }: { amount: number }) {
  return (
    <span className="rlt-rail-coin" aria-hidden>
      <span className="rlt-coin" data-v={String(amount)}>
        <span className="rlt-coin-face">
          {amount >= 1000 ? `${+(amount / 1000).toFixed(1)}k` : amount}
        </span>
      </span>
    </span>
  );
}

export default Board;
export type { BetKind };
